import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Music, Search, X } from 'lucide-react';
import clsx from 'clsx';
import { musicApi, MusicTrack } from '@/api/music';

/**
 * Выбор фоновой музыки к story.  Два режима сортировки + поиск.
 */
export default function MusicPicker({
  value,
  onChange,
  onClose,
}: {
  value: string | null;
  onChange: (track: MusicTrack | null) => void;
  onClose: () => void;
}) {
  const [sort, setSort] = useState<'popular' | 'recent'>('popular');
  const [q, setQ] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useState(() => new Audio())[0];

  const { data } = useQuery({
    queryKey: ['music', sort, q],
    queryFn: () => musicApi.list({ sort, q: q.trim() || undefined, limit: 40 }),
  });

  useEffect(() => () => { audioRef.pause(); audioRef.src = ''; }, [audioRef]);

  const togglePreview = (t: MusicTrack) => {
    if (playingId === t.id) {
      audioRef.pause();
      setPlayingId(null);
    } else {
      audioRef.src = t.audioUrl;
      audioRef.currentTime = 0;
      audioRef.play().catch(() => {/* ignore */});
      setPlayingId(t.id);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center">
      <div className="w-full sm:max-w-md bg-surface-900 rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-surface-800">
          <h2 className="font-semibold">Музыка</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-3 space-y-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Название или исполнитель"
              className="w-full bg-surface-800 rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2 text-xs">
            {(['popular', 'recent'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSort(s)}
                className={clsx(
                  'px-3 py-1 rounded-full',
                  sort === s ? 'bg-brand-500 text-white' : 'bg-surface-800 text-surface-400',
                )}
              >
                {s === 'popular' ? 'Популярные' : 'Новые'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {value && (
            <button
              onClick={() => onChange(null)}
              className="w-full text-left px-4 py-2 text-sm text-surface-400 border-b border-surface-800"
            >
              Убрать музыку
            </button>
          )}
          {(data?.items ?? []).map((t) => (
            <div
              key={t.id}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 border-b border-surface-800 cursor-pointer',
                value === t.id && 'bg-surface-800',
              )}
              onClick={() => { onChange(t); onClose(); }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); togglePreview(t); }}
                className="w-10 h-10 rounded-lg bg-surface-700 flex items-center justify-center relative overflow-hidden"
              >
                {t.coverUrl ? <img src={t.coverUrl} className="absolute inset-0 w-full h-full object-cover" /> : null}
                <Music size={16} className="relative z-10 text-white" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{t.title}</div>
                <div className="text-xs text-surface-400 truncate">{t.artist}</div>
              </div>
              <div className="text-xs text-surface-500">{formatDuration(t.durationSec)}</div>
            </div>
          ))}
          {(!data || data.items.length === 0) && (
            <div className="p-8 text-center text-surface-500 text-sm">Треков не найдено</div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDuration(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
